import './styles.scss';

import { Button, Card } from 'react-bootstrap';
import { processBulkPromises, uploadFile } from '@/utils/promises';
import { useEffect, useState } from 'react';

import CardHeader from '@/features/DriveTimeTool/components/CardHeader';
import FileInput from '@/common/FileInput';
import { ImportStatusEnum } from '@/models/gen/graphql';
import { LoadingBlur } from '@/components/LoadingSpinner';
import TippyWhen from '@/components/TippyWhen';
import { bytesToSize } from '@/utils/strings';
import createDtrImport from '@/features/DriveTimeTool/api/services/createDtrImport';
import md5 from 'md5';
import updateImportsStatus from '@/api/services/imports/updateImportsStatus';
import { useNavigate } from 'react-router-dom';
import useSession from '@/state/session';

const ManifestUpload = (): JSX.Element => {
  const [files, setFiles] = useState<
    Map<string, { file: File; id: string; progress: number; error?: string; url: string; md5?: string; contentType: string }>
  >(new Map());
  const [status, setStatus] = useState<'success' | 'error' | 'processing' | 'ready'>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const supportedManifestFileTypes = useSession(({ state }) => state.config?.supportedManifestFileTypes || []);
  const isFileSupported = (file: File) => {
    const fileExtensionRegex = /[0-9a-z]+$/i;
    return (supportedManifestFileTypes || []).includes(file.name.match(fileExtensionRegex)[0]);
  };

  const handleFileChange = async (input: File | File[] | FileList) => {
    try {
      setLoading(true);
      const files = input instanceof FileList ? Array.from(input) : Array.isArray(input) ? input : [input];
      const md5ToFileMap = new Map();
      await processBulkPromises(files, async (file) => {
        const id = md5(file.name, file.size, file.type);
        try {
          if (md5ToFileMap.has(id)) return;
          if (!isFileSupported(file)) throw new Error('File type not supported');
          const res = await createDtrImport(file.name, id);
          const edge = res?.imports?.[0] || {};
          if (edge.error) throw new Error(`${edge.error.code}:${edge.error.message}`);
          // if the file is already uploaded set to progress to 100
          if (!edge.import?.url && edge.import?.fileLocation) {
            md5ToFileMap.set(id, {
              id: edge.import?.id,
              file,
              progress: 100,
              md5: id,
            });
            return;
          }
          if (!edge.import?.url) throw new Error('No upload url provided. Please try again');
          if (!edge.import?.contentType) throw new Error('No content type provided. Please try again');
          md5ToFileMap.set(id, {
            id: edge.import?.id,
            file,
            progress: 0,
            url: edge.import?.url,
            contentType: edge.import?.contentType,
            md5: id,
          });
        } catch (err) {
          console.error('ManifestUpload: handleFileChange:', err?.message || err);
          md5ToFileMap.set(id, { file, md5: id, progress: 0, error: err?.message || err });
        }
      });
      setFiles(md5ToFileMap);
      if (Array.from(md5ToFileMap.values()).some((bundle) => bundle.error)) return setStatus('error');
      setStatus('ready');
    } catch (err) {
      console.error('ManifestUpload: handleFileChange:', err?.message || err);
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!files.size) return;
    setStatus('processing');
    await processBulkPromises(Array.from(files.values()), async (file) => {
      try {
        if (file.error) return;
        // if the file is not already uploaded we will upload the file
        if (file.progress !== 100) {
          const res = await uploadFile(file, {
            onUploadProgress: (progressEvent) => {
              const { loaded, total } = progressEvent;
              const percent = (loaded * 100) / total;
              setFiles((current) => current.set(file.md5, { ...file, progress: percent }));
            },
          });
          if (res.status !== 200) throw new Error('failed to upload file try again');
        }
        const updateImportResponse = await updateImportsStatus([file.id], ImportStatusEnum.Uploaded, '');
        if (!updateImportResponse) throw new Error('failed to update import status');
        if (updateImportResponse?.imports?.[0]?.error) throw new Error(updateImportResponse.imports[0].error.message);
      } catch (err) {
        console.error('ManifestUpload: handleSubmit:', err?.message || err);
        setFiles((current) => current.set(file.md5, { ...file, error: err?.message || err }));
      }
    });
    setFiles((current) => {
      setStatus(Array.from(current.values()).some((bundle) => bundle.error) ? 'error' : 'success');
      return current;
    });
  };

  useEffect(() => {
    if (status !== 'success' || !files.size) return;
    navigate(`/tools/drive-time/imports/${Array.from(files.values())[0].id}`);
  }, [status]);

  return (
    <div className={`ManifestUpload status-${status}`}>
      <Card className="manifest-upload-card">
        <LoadingBlur loading={loading} />
        <CardHeader icon="fas fa-upload" subtitle="Upload a manifest to create drive times based on historical data.">
          Manifest Upload
        </CardHeader>
        <Card.Body className="d-flex flex-column gap-1">
          <FileInput
            onChange={handleFileChange}
            className={files.size > 0 ? 'justify-content-between' : 'justify-content-center'}
            disabled={loading}
          >
            <>
              <div className="d-flex flex-column align-items-center mb-1 gap-2">
                <span className="rounded-circle icon-container p-4 d-flex justify-content-center align-items-center">
                  <i className={`sv sv-${status === 'error' || status === 'success' ? 'checkmark-circle' : 'upload2'} fs-1`} />
                </span>
                <span className="text-muted">
                  {status === 'error'
                    ? 'Upload Failed... Please Try Again'
                    : status === 'success'
                      ? 'Manifest Uploaded Successfully'
                      : 'Drop Manifest File Here or click to browse files'}
                </span>
                <span className="text-muted">{status !== 'error' && status !== 'success' && 'Supports csv, html, and pdf files'}</span>
              </div>
              {files.size > 0 && (
                <div className="w-100">
                  <div className="mb-2">
                    <h5>UPLOADED FILE</h5>
                  </div>
                  <div className="d-flex flex-column mb-1 gap-3">
                    {Array.from(files.values()).map((bundle, idx) => (
                      <File
                        key={idx}
                        name={bundle.file.name}
                        size={bundle.file.size}
                        error={bundle.error}
                        progress={bundle.progress}
                        status={status}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          </FileInput>
          <hr />
          <div className="d-flex gap-3">
            {files.size > 0 && (
              <Button
                className="submit-btn"
                variant="secondary"
                onClick={(e) => {
                  setFiles(new Map());
                  setStatus(null);
                  setLoading(false);
                }}
              >
                Clear
              </Button>
            )}
            <Button
              variant="primary"
              className="submit-btn"
              onClick={handleSubmit}
              disabled={!files.size || status === 'processing' || Array.from(files.values()).some((f) => f.error)}
            >
              {status !== 'processing' && files.size === 0 && (
                <>
                  <i className="sv sv-upload2 me-2" /> Upload a Manifest First
                </>
              )}
              {status !== 'processing' && files.size > 0 && (
                <>
                  <i className="sv sv-upload2 me-2" /> Import Manifest & Continue
                </>
              )}
              {status === 'processing' && (
                <>
                  <i className="sv sv-return2 me-2 fa-spin fa-spin-reverse" />
                  Processing
                </>
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};
const fileExtensionRegex = /[0-9a-z]+$/i;
const File = ({
  name,
  size,
  error,
  progress,
  status,
}: {
  name: string;
  size: number;
  error?: string;
  progress?: number;
  status?: string;
}) => {
  const ext = name.match(fileExtensionRegex)[0] || 'UNKNOWN';
  const getStatus = () => {
    if (error) return 'Upload Failed';
    if (progress === 100) return 'Done';
    if (progress > 0 && progress < 100) return 'Uploading...';
    if (status === 'ready') return 'Ready';
    return 'Pending';
  };
  const getVariant = () => {
    if (error) return 'danger';
    if (progress === 100) return 'success';
    if (progress > 0 && progress < 100) return 'cyan';
    if (status === 'ready') return 'success';
    return 'cyan';
  };
  return (
    <div className={`File status-${getVariant()} d-flex p-3 shadow rounded-2 flex-nowrap`}>
      <span className="p-3 icon-container shadow rounded-3 d-flex justify-content-center align-items-center me-3">
        <i className="sv sv-document fs-3" />
      </span>

      <div className="d-flex flex-column gap-3 flex-grow-1 h-100 ">
        <div>
          <strong className="me-3">{name}</strong>
          <TippyWhen isTrue={!!error} options={{ content: error }}>
            <span className={`text-${getVariant()}`}>
              <i className={`sv sv-${!!error || progress === 100 ? 'checkmark-circle' : 'return2'} me-2`} />
              {getStatus()}
            </span>
          </TippyWhen>
        </div>

        <span className="text-muted">
          {bytesToSize(size)} &middot; {ext.toUpperCase()} File
        </span>
      </div>
    </div>
  );
};

export default ManifestUpload;
